/* eslint-disable consistent-return */
/* eslint-disable max-classes-per-file */
import { ClientError } from 'graphql-request';
import { GraphQLError } from 'graphql/error';

export class UnauthenticatedError extends Error {
  name: string = 'UnauthenticatedError';
}

export class RefreshTokenError extends Error {
  name: string = 'RefreshTokenError';
}

export class InternalServerError extends Error {
  name: string = 'InternalServerError';
}

export class UnexpectedError extends Error {
  name: string = 'UnexpectedError';
}

export class GqlErrors extends Error {
  name: string = 'GqlErrors';

  errors: GraphQLError[];

  constructor(
    message: string | undefined,
    options: ErrorOptions | undefined,
    errors: GraphQLError[],
  ) {
    super(message, options);
    this.errors = errors;
  }
}

export class NetworkError extends Error {
  name: string = 'NetworkError';
}

export type SingleInvalidInputError = {
  field: string;
  message: string;
};

export class InvalidInputError extends Error {
  name: string = 'InvalidInputError';

  errors: SingleInvalidInputError[];

  constructor(name: string, errors: SingleInvalidInputError[]) {
    super(name);
    this.errors = errors;
  }
}

export type ErrorType =
  | RefreshTokenError
  | UnauthenticatedError
  | GqlErrors
  | NetworkError
  | InternalServerError
  | UnexpectedError
  | InvalidInputError;

export function parseError(error: unknown): ErrorType {
  try {
    const originalError = JSON.stringify(error, null, 2);
    if (error instanceof Error && error.message === 'Failed to fetch') {
      return new NetworkError(
        'Network Error: Please check your connection and try again.',
        { cause: originalError },
      );
    }

    if (error instanceof ClientError && error.response.status === 401) {
      return new UnauthenticatedError('Unauthorized request', {
        cause: originalError,
      });
    }

    if (
      error instanceof ClientError &&
      error.response.errors?.some(
        (err) =>
          err.message === 'Access denied' ||
          err.extensions?.http_status_code === 401,
      )
    ) {
      return new UnauthenticatedError('Unauthorized request', {
        cause: originalError,
      });
    }

    if (
      error instanceof ClientError &&
      error.response.errors?.some((err) => err.message === 'validation')
    ) {
      // TODO: Check again if have multiple graphql error
      const validationErrors = error.response.errors[0]?.extensions
        ?.validation as {
        [key: string]: Array<{ message: string }>;
      };
      const normalizeErrors: SingleInvalidInputError[] = Object.keys(
        validationErrors,
      ).map((key) => ({
        field: key,
        message: validationErrors?.[key]?.[0]?.message || '',
      }));
      return new InvalidInputError('Invalid input', normalizeErrors);
    }

    if (
      error instanceof ClientError &&
      error.response.errors?.some(
        (err) => err.message === 'Internal server error',
      )
    ) {
      return new InternalServerError('Something went wrong', {
        cause: originalError,
      });
    }

    if (error instanceof ClientError && error.response.errors) {
      return new GqlErrors(
        'Something went wrong',
        { cause: originalError },
        error.response.errors,
      );
    }

    return new UnexpectedError('Something went wrong', {
      cause: originalError,
    });
  } catch (err) {
    return new UnexpectedError('Something went wrong', {
      cause: err,
    });
  }
}

export function handleError({ error }: { error: ErrorType }) {
  try {
    if (error instanceof RefreshTokenError) {
      return;
    }
    if (error instanceof InvalidInputError) {
      return error;
    }

    if (
      error instanceof UnauthenticatedError ||
      error instanceof UnexpectedError ||
      error instanceof InternalServerError ||
      error instanceof NetworkError
    ) {
      return error;
    }

    // Now error is GraphqlError[]
    const validationError = error.errors.find(
      (err) => !!err.extensions.validation,
    );

    if (validationError) {
      // TODO: Check again if have multiple graphql error
      const validationErrors = validationError.extensions.validation as {
        [key: string]: Array<{ message: string }>;
      };

      const normalizeErrors = Object.keys(validationErrors).map((key) => ({
        field: key,
        type: 'server',
        message: validationErrors?.[key]?.[0]?.message || '',
      }));
      return new InvalidInputError('Invalid input', normalizeErrors);
    }
    // Unknown graphql error
    if (error.errors.some((err) => err.extensions.validation === undefined)) {
      return new UnexpectedError('Something went wrong', {
        cause: error,
      });
    }
  } catch (err) {
    return new UnexpectedError('Something went wrong', {
      cause: err,
    });
  }
}
